import { useState, useMemo } from 'react';
import { Box, Paper, Typography, Chip, Button, Menu, MenuItem } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { knownDevices } from '../data/networkData';
import { mockSecurityData, hashStr } from './DeviceDetail';

// ── Build device C2C data ──────────────────────────────────────

function buildC2CList() {
  const seen = new Set();
  return Object.entries(knownDevices)
    .filter(([, d]) => { if (seen.has(d.hostname)) return false; seen.add(d.hostname); return true; })
    .map(([ip, d]) => {
      const mock = mockSecurityData(d.hostname);
      const h = hashStr(d.hostname);
      const daysAgo = (h >>> 20) % 8;
      const posture = mock.c2cStatus === 'Compliant'
        ? (h % 5 === 0 ? 'Pending' : 'Passed')
        : 'Failed';
      return {
        hostname: d.hostname,
        ip,
        os: d.os,
        type: d.type,
        c2cStatus: mock.c2cStatus,
        posture,
        lastCheckin: daysAgo === 0 ? 'Today' : `${daysAgo} day${daysAgo > 1 ? 's' : ''} ago`,
        daysAgo,
      };
    })
    .sort((a, b) => {
      if (a.c2cStatus !== b.c2cStatus) return a.c2cStatus === 'Non-Compliant' ? -1 : 1;
      return a.daysAgo - b.daysAgo;
    });
}

const DEVICES = buildC2CList();

// ── ColFilter ──────────────────────────────────────────────────

function ColFilter({ label, options, value, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const isFiltered = value.length > 0;
  const toggle = (val) => onChange(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <Typography sx={{ color: isFiltered ? '#00bcd4' : '#7a8fa6', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}{isFiltered ? ` (${value.length})` : ''}
      </Typography>
      {options.length > 0 && (
        <>
          <Box onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: isFiltered ? '#00bcd4' : '#1e3a5f', '&:hover': { color: '#00bcd4' } }}>
            <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
          </Box>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
            PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f', minWidth: 160 } }}>
            {isFiltered && (
              <MenuItem onClick={() => { onChange([]); setAnchor(null); }} sx={{ fontSize: 11, color: '#f44336', borderBottom: '1px solid #1e3a5f', '&:hover': { backgroundColor: '#0d2137' } }}>
                Clear filter
              </MenuItem>
            )}
            {options.map(opt => {
              const selected = value.includes(opt);
              return (
                <MenuItem key={opt} onClick={() => toggle(opt)}
                  sx={{ fontSize: 12, color: selected ? '#00bcd4' : '#c0cfe0', backgroundColor: selected ? '#0d2137' : 'transparent', '&:hover': { backgroundColor: '#0d2137' }, display: 'flex', justifyContent: 'space-between' }}>
                  {opt}
                  {selected && <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#00bcd4' }} />}
                </MenuItem>
              );
            })}
          </Menu>
        </>
      )}
    </Box>
  );
}

function StatBox({ label, value, color }) {
  return (
    <Paper sx={{ flex: 1, backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, padding: '16px 20px', textAlign: 'center' }}>
      <Typography sx={{ color: color || '#ffffff', fontSize: 36, fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 0.5 }}>{label}</Typography>
    </Paper>
  );
}

export default function ComplyToConnect() {
  const [filterOS, setFilterOS] = useState([]);
  const [filterType, setFilterType] = useState([]);
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterPosture, setFilterPosture] = useState([]);

  const stats = useMemo(() => ({
    total:         DEVICES.length,
    compliant:     DEVICES.filter(d => d.c2cStatus === 'Compliant').length,
    nonCompliant:  DEVICES.filter(d => d.c2cStatus === 'Non-Compliant').length,
    pending:       DEVICES.filter(d => d.posture === 'Pending').length,
  }), []);

  const osOptions = useMemo(() => Array.from(new Set(DEVICES.map(d => d.os))).sort(), []);

  const filteredDevices = useMemo(() => DEVICES.filter(d => {
    if (filterOS.length > 0 && !filterOS.includes(d.os)) return false;
    if (filterType.length > 0 && !filterType.includes(d.type)) return false;
    if (filterStatus.length > 0 && !filterStatus.includes(d.c2cStatus)) return false;
    if (filterPosture.length > 0 && !filterPosture.includes(d.posture)) return false;
    return true;
  }), [filterOS, filterType, filterStatus, filterPosture]);

  return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', padding: 3 }}>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, marginBottom: 3 }}>
        <StatBox label="Total Devices"  value={stats.total}        color="#ffffff" />
        <StatBox label="Compliant"      value={stats.compliant}    color="#4caf50" />
        <StatBox label="Non-Compliant"  value={stats.nonCompliant} color="#f44336" />
        <StatBox label="Pending"        value={stats.pending}      color="#ff9800" />
      </Box>

      {/* Table */}
      <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, overflow: 'hidden' }}>

        {/* Header */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '160px 130px 1fr 90px 110px 120px 90px 100px', padding: '10px 16px', borderBottom: '1px solid #1e3a5f', backgroundColor: '#0d1825' }}>
          <ColFilter label="Device"       options={[]}                          value={[]}            onChange={() => {}} />
          <ColFilter label="IP"           options={[]}                          value={[]}            onChange={() => {}} />
          <ColFilter label="OS"           options={osOptions}                   value={filterOS}      onChange={setFilterOS} />
          <ColFilter label="Type"         options={['Physical', 'Virtual']}     value={filterType}    onChange={setFilterType} />
          <ColFilter label="C2C Status"   options={['Compliant', 'Non-Compliant']} value={filterStatus} onChange={setFilterStatus} />
          <ColFilter label="Last Check-in" options={[]}                         value={[]}            onChange={() => {}} />
          <ColFilter label="Posture"      options={['Passed', 'Failed', 'Pending']} value={filterPosture} onChange={setFilterPosture} />
          <ColFilter label="Action"       options={[]}                          value={[]}            onChange={() => {}} />
        </Box>

        {/* Rows */}
        {filteredDevices.map((d, i) => (
          <Box key={d.hostname} sx={{
            display: 'grid',
            gridTemplateColumns: '160px 130px 1fr 90px 110px 120px 90px 100px',
            alignItems: 'center',
            padding: '11px 16px',
            borderBottom: i < filteredDevices.length - 1 ? '1px solid #0d1825' : 'none',
            '&:hover': { backgroundColor: '#0d1f33' },
            backgroundColor: d.c2cStatus === 'Non-Compliant' ? '#1a0a0a' : 'transparent',
          }}>
            <Typography sx={{ color: '#ffffff', fontWeight: 600, fontFamily: 'monospace', fontSize: 13 }}>{d.hostname}</Typography>
            <Typography sx={{ color: '#00bcd4', fontFamily: 'monospace', fontSize: 12 }}>{d.ip}</Typography>
            <Typography sx={{ color: '#7a8fa6', fontSize: 12 }}>{d.os}</Typography>
            <Chip label={d.type} size="small" sx={{
              backgroundColor: d.type === 'Virtual' ? '#2a1f4a' : '#1e3a5f',
              color: d.type === 'Virtual' ? '#a855f7' : '#00bcd4',
              fontWeight: 700, fontSize: 11, height: 20,
            }} />
            <Chip label={d.c2cStatus} size="small" sx={{
              backgroundColor: d.c2cStatus === 'Compliant' ? '#0a2a1a' : '#2a0a0a',
              color: d.c2cStatus === 'Compliant' ? '#4caf50' : '#f44336',
              fontWeight: 700, fontSize: 11, height: 20,
            }} />
            <Typography sx={{ color: d.daysAgo > 3 ? '#ff9800' : '#7a8fa6', fontSize: 13 }}>{d.lastCheckin}</Typography>
            <Chip label={d.posture} size="small" sx={{
              backgroundColor: d.posture === 'Passed' ? '#0a2a1a' : d.posture === 'Failed' ? '#2a0a0a' : '#1a1a0a',
              color: d.posture === 'Passed' ? '#4caf50' : d.posture === 'Failed' ? '#f44336' : '#ff9800',
              fontWeight: 700, fontSize: 11, height: 20,
            }} />
            <Button size="small" variant="outlined" sx={{
              borderColor: '#1e3a5f', color: '#7a8fa6', fontSize: 11, padding: '2px 10px',
              '&:hover': { borderColor: '#00bcd4', color: '#00bcd4' },
            }}>
              Re-check
            </Button>
          </Box>
        ))}
      </Paper>
    </Box>
  );
}
